import React, { useState } from "react";
import {
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
  Box,
  Popover,
  Stack,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Science,
  Biotech,
  KeyboardArrowDown,
  Download,
} from "@mui/icons-material";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { dosyaAdi } from "../../utils/exportUtils";
import api from "../../api";

// --- GÖRSEL YARDIMCI: EXCELJS İLE ZARİF TABLO ÇİZİMİ ---
const applyLabTableStyles = (worksheet, dataArray) => {
  if (dataArray.length === 0) return;

  // 1. Sütun Genişlikleri
  worksheet.columns = [
    { key: "col1", width: 25 }, // Örn: Numune No
    { key: "col2", width: 18 }, // Örn: Derinlik
    { key: "col3", width: 40 }, // Örn: Koordinat / Açıklama
    { key: "col4", width: 30 }, // Örn: Diğer
  ];

  // Veri başlıklarını al (objenin key'leri)
  const headers = Object.keys(dataArray[0]);

  // 2. Başlık Satırını Ekle ve Şekillendir
  const headerRow = worksheet.addRow(headers);
  headerRow.height = 25;
  headerRow.eachCell((cell) => {
    cell.font = {
      bold: true,
      color: { argb: "FFFFFFFF" },
      size: 10,
      name: "Arial",
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF4F81BD" }, // Koyu Mavi Başlık
    };
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };
    cell.border = {
      top: { style: "thin", color: { argb: "FF000000" } },
      left: { style: "thin", color: { argb: "FF000000" } },
      bottom: { style: "medium", color: { argb: "FF000000" } },
      right: { style: "thin", color: { argb: "FF000000" } },
    };
  });

  // 3. Veri Satırlarını Ekle ve Zebra Deseni Uygula
  dataArray.forEach((rowObj, index) => {
    const rowValues = headers.map((header) => rowObj[header]);
    const row = worksheet.addRow(rowValues);
    row.height = 20;

    // Tek/Çift satır renklendirmesi (Zebra)
    const isEven = index % 2 === 0;

    row.eachCell((cell) => {
      cell.font = { size: 9, name: "Arial" };
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };

      if (isEven) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFF2F2F2" },
        }; // Açık Gri
      }

      cell.border = {
        top: { style: "thin", color: { argb: "FFD9D9D9" } },
        left: { style: "thin", color: { argb: "FFD9D9D9" } },
        bottom: { style: "thin", color: { argb: "FFD9D9D9" } },
        right: { style: "thin", color: { argb: "FFD9D9D9" } },
      };
    });
  });
};

export default function LabExportExcelButton({ jobId, jobName }) {
  const theme = useTheme();

  // Açılır Popover State'leri
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Yükleme State'leri (İkisi ayrı dönsün diye)
  const [loadingProf, setLoadingProf] = useState(false);
  const [loadingSonda, setLoadingSonda] = useState(false);

  // Menü Aç/Kapat
  const handlePopoverClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  // 1. SADECE PROFİL İÇİN EXCEL İNDİRME
  const handleExportProfile = async () => {
    setLoadingProf(true);
    try {
      const res = await api.get(`/admin/export-lab/${jobId}`);
      const data = res.data.success ? res.data.data : [];

      if (data.length <= 2) {
        alert(
          "Bu çalışmaya ait laboratuvara gönderilecek PROFİL numunesi bulunamadı.",
        );
        setLoadingProf(false);
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Profil_Numuneleri", {
        views: [{ showGridLines: false }], // Izgara çizgilerini gizle
      });

      // Tasarımı Uygula
      applyLabTableStyles(worksheet, data);

      // İndirme İşlemi
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer]),
        dosyaAdi(jobName, "Profil Lab Verisi", "xlsx"),
      );
    } catch (error) {
      console.error(error);
      alert("Profil Excel oluşturulurken hata oluştu.");
    } finally {
      setLoadingProf(false);
      handlePopoverClose(); // İşlem bitince kutuyu kapat
    }
  };

  // 2. SADECE SONDA İÇİN EXCEL İNDİRME
  const handleExportSonda = async () => {
    setLoadingSonda(true);
    try {
      const res = await api.get(`/admin/export-lab-sonda/${jobId}`);
      const data = res.data.success ? res.data.data : [];

      if (data.length <= 2) {
        alert(
          "Bu çalışmaya ait laboratuvara gönderilecek SONDA numunesi bulunamadı.",
        );
        setLoadingSonda(false);
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Sonda_Numuneleri", {
        views: [{ showGridLines: false }], // Izgara çizgilerini gizle
      });

      // Tasarımı Uygula
      applyLabTableStyles(worksheet, data);

      // İndirme İşlemi
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer]),
        dosyaAdi(jobName, "Sonda Lab Verisi", "xlsx"),
      );
    } catch (error) {
      console.error(error);
      alert("Sonda Excel oluşturulurken hata oluştu.");
    } finally {
      setLoadingSonda(false);
      handlePopoverClose(); // İşlem bitince kutuyu kapat
    }
  };

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      {/* ANA TETİKLEYİCİ BUTON (Yuvarlak) */}
      <Tooltip title="Laboratuvar İçin Excel İndir (Profil & Sonda)">
        <IconButton
          color="info"
          onClick={handlePopoverClick}
          disabled={loadingProf || loadingSonda}
          sx={{
            bgcolor: alpha(theme.palette.info.main, 0.05),
            mb: 0.5,
            "&:hover": { bgcolor: alpha(theme.palette.info.main, 0.15) },
          }}
        >
          {loadingProf || loadingSonda ? (
            <CircularProgress size={20} color="info" />
          ) : (
            <Download fontSize="small" />
          )}
        </IconButton>
      </Tooltip>

      <Typography
        variant="caption"
        sx={{
          fontSize: "0.6rem",
          color: "text.secondary",
          fontWeight: "bold",
          display: "flex",
          alignItems: "center",
        }}
      >
        LAB İNDİR <KeyboardArrowDown sx={{ fontSize: "0.8rem" }} />
      </Typography>

      {/* AÇILAN KUTU (İçinde yuvarlak ikonlar olacak) */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{ sx: { p: 2, mt: 1, borderRadius: 3, boxShadow: 4 } }}
      >
        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "center",
            mb: 2,
            color: "text.secondary",
            fontWeight: "bold",
          }}
        >
          İNDİRİLECEK VERİYİ SEÇİN
        </Typography>

        <Stack
          direction="row"
          spacing={3}
          alignItems="center"
          justifyContent="center"
        >
          {/* PROFİL YUVARLAK BUTONU */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Tooltip title="Profil Lab Verisi İndir">
              <IconButton
                color="primary"
                onClick={handleExportProfile}
                disabled={loadingProf}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  mb: 0.5,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.15),
                  },
                }}
              >
                {loadingProf ? (
                  <CircularProgress size={20} color="primary" />
                ) : (
                  <Science fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.6rem",
                color: "text.secondary",
                fontWeight: "bold",
              }}
            >
              P LAB
            </Typography>
          </Box>

          {/* SONDA YUVARLAK BUTONU */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Tooltip title="Sonda Lab Verisi İndir">
              <IconButton
                color="secondary"
                onClick={handleExportSonda}
                disabled={loadingSonda}
                sx={{
                  bgcolor: alpha(theme.palette.secondary.main, 0.05),
                  mb: 0.5,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.secondary.main, 0.15),
                  },
                }}
              >
                {loadingSonda ? (
                  <CircularProgress size={20} color="secondary" />
                ) : (
                  <Biotech fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.6rem",
                color: "text.secondary",
                fontWeight: "bold",
              }}
            >
              S LAB
            </Typography>
          </Box>
        </Stack>
      </Popover>
    </Box>
  );
}
